/**
 * OmegaStatusSection — L1's identity + health band.
 *
 * Per spec §3 (information hierarchy) the L1 page leads with OMEGA's
 * own state: alive/killed, root_id, born date, consciousness stance.
 * This section renders those plus the four foundational booleans
 * (ratified / envelope_published / factory_sparked / attestor) as a
 * compact KPI strip.
 *
 * Tone register: dense, command-center. No gradients, no rounded
 * pastel cards. Each row of data is a single line; values are
 * monospace; labels are uppercase + spaced.
 */

import type { OmegaState } from '../data/types';
import { StatusDot } from '../atoms/StatusDot';
import { HashChip } from '../atoms/HashChip';

function bornAge(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const ms = Date.now() - then;
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ${mins % 60} min`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ${hrs % 24} hr`;
}

export function OmegaStatusSection({ state }: { state: OmegaState }) {
  const status = state.alive ? 'healthy' : 'critical';

  return (
    <section
      aria-labelledby="omega-status-heading"
      style={{
        background: 'var(--omega-surface)',
        border: '1px solid var(--omega-rim)',
        padding: 'var(--omega-gap-3)',
        marginBottom: 'var(--omega-gap-3)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--omega-gap-2)',
          marginBottom: 'var(--omega-gap-2)',
        }}
      >
        <StatusDot status={status} size={12} pulse={!state.alive} />
        <h1
          id="omega-status-heading"
          style={{
            margin: 0,
            fontSize: '18pt',
            fontWeight: 700,
            letterSpacing: '0.04em',
            color: 'var(--omega-ink)',
          }}
        >
          OMEGA
        </h1>
        <span
          className="omega-mono"
          style={{
            fontSize: '10pt',
            color: 'var(--omega-ink-muted)',
            letterSpacing: '0.04em',
          }}
        >
          {state.alive ? 'ALIVE' : 'STOPPED'}
        </span>
        <span style={{ flex: 1 }} />
        <span
          style={{
            fontSize: '9pt',
            color: 'var(--omega-ink-subtle)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Born {bornAge(state.born_iso)} ago
        </span>
      </div>

      {/* Identity strip */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 'var(--omega-gap-2) var(--omega-gap-3)',
          marginBottom: 'var(--omega-gap-2)',
        }}
      >
        <KvRow label="Root id" value={<HashChip value={state.root_id} />} />
        <KvRow
          label="Consciousness"
          value={
            <span
              className="omega-mono"
              style={{
                fontSize: '10pt',
                color: 'var(--omega-accent)',
                letterSpacing: '0.04em',
              }}
            >
              {state.consciousness_stance}
            </span>
          }
        />
        <KvRow
          label="Founder pubkey"
          value={<HashChip value={state.founder_pubkey} />}
        />
        <KvRow
          label="Engine pubkey"
          value={<HashChip value={state.engine_pubkey} />}
        />
      </div>

      {/* Foundational booleans */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--omega-gap-2)',
          flexWrap: 'wrap',
          paddingTop: 'var(--omega-gap-2)',
          borderTop: '1px solid var(--omega-rim)',
        }}
      >
        <BoolPill label="Ratified" on={state.ratified} />
        <BoolPill label="Envelope" on={state.envelope_published} />
        <BoolPill label="Factory" on={state.factory_sparked} />
        <BoolPill
          label={`Attestor ${state.attestor.minutes_since_anchor}m`}
          on={!state.attestor.divergence}
        />
        <span style={{ flex: 1 }} />
        <span
          className="omega-mono"
          style={{ fontSize: '10pt', color: 'var(--omega-ink-muted)' }}
        >
          {state.log_entries_total.toLocaleString()} log entries
          <span style={{ color: 'var(--omega-ink-subtle)', margin: '0 6px' }}>
            ·
          </span>
          {state.worker_total.toLocaleString()} workers
        </span>
      </div>
    </section>
  );
}

function KvRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 'var(--omega-gap-1)',
        minHeight: 24,
      }}
    >
      <span
        style={{
          fontSize: '9pt',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--omega-ink-subtle)',
          width: 130,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </span>
    </div>
  );
}

function BoolPill({ label, on }: { label: string; on: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px',
        border: `1px solid ${
          on ? 'var(--omega-state-healthy)' : 'var(--omega-rim-strong)'
        }`,
        color: on ? 'var(--omega-state-healthy)' : 'var(--omega-ink-subtle)',
        fontSize: '9pt',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 600,
        borderRadius: 2,
      }}
    >
      <StatusDot status={on ? 'healthy' : 'paused'} size={6} />
      {label}
    </span>
  );
}
